import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, MapPin, Clock, ArrowRight, Users, ImageIcon,
  Loader2, Camera
} from 'lucide-react';
import axios from 'axios';
import logger from '../utils/logger';
import ActiveEventRSVP from './components/ActiveEventRSVP';
import PhotoLightbox from '../components/PhotoLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FeaturedGallery = ({ gallery, photos }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  if (!gallery || !photos.length) return null;

  return (
    <section className="py-12 bg-[#0A0A0A]" data-testid="featured-gallery-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">Gallery</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
            {gallery.event_title}
          </h2>
          <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
            {gallery.event_date && (
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{gallery.event_date}</span>
            )}
            {gallery.event_location && (
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{gallery.event_location}</span>
            )}
            <span className="flex items-center gap-1"><Camera className="w-3.5 h-3.5" />{photos.length} photos</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setLightboxIdx(i)}
              className="aspect-square rounded-lg overflow-hidden bg-white/5 group relative"
              data-testid={`featured-photo-${p.id}`}
            >
              <img src={p.image_url} alt={p.guest_name || 'Event moment'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              {p.guest_name && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-[10px] text-white truncate">{p.guest_name}</p>
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="text-center mt-6">
          <Link
            to={`/gallery/${gallery.id}/upload`}
            className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors"
            data-testid="view-full-gallery-link"
          >
            <Camera className="w-3.5 h-3.5" /> View full gallery
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Phase D: unified PhotoLightbox — object-contain, mobile-safe
          100dvh cap, ESC/backdrop/close all consistent. */}
      <PhotoLightbox
        photos={photos.map((p) => p.image_url)}
        open={lightboxIdx !== null}
        startIndex={lightboxIdx || 0}
        title={gallery.event_title}
        onClose={() => setLightboxIdx(null)}
      />
    </section>
  );
};

const EventsPage = () => {
  const [upcoming, setUpcoming] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [featuredGallery, setFeaturedGallery] = useState(null);
  const [featuredPhotos, setFeaturedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, photos: [], idx: 0, title: '' });

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, activeRes, featuredRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/events/all`),
        axios.get(`${BACKEND_URL}/api/public/events/active`),
        axios.get(`${BACKEND_URL}/api/public/galleries/featured`).catch(() => ({ data: { gallery: null } })),
      ]);
      setUpcoming(eventsRes.data.upcoming || []);
      setCompleted(eventsRes.data.completed || []);
      if (activeRes.data.active && activeRes.data.event) {
        setActiveEvent(activeRes.data.event);
      }
      if (featuredRes.data.gallery) {
        setFeaturedGallery(featuredRes.data.gallery);
        // Fetch featured photos (only those marked as featured, fallback to all approved)
        try {
          const photosRes = await axios.get(`${BACKEND_URL}/api/public/galleries/${featuredRes.data.gallery.id}/photos`);
          const allPhotos = photosRes.data || [];
          const featured = allPhotos.filter(p => p.is_featured);
          setFeaturedPhotos(featured.length > 0 ? featured : allPhotos.slice(0, 12));
        } catch {}
      }
    } catch (error) {
      logger.error('Error fetching events data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <section className="bg-[#0A0A0A] py-12" data-testid="events-header">
        <div className="px-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">Community</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3" data-testid="events-page-title">
            Our Events
          </h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            Join us at our upcoming events. Every gathering is an opportunity to serve, connect, and create real change together.
          </p>
        </div>
      </section>

      {/* Active Event RSVP */}
      {activeEvent && <ActiveEventRSVP event={activeEvent} />}

      {/* Upcoming Events */}
      <section className="py-10 bg-white" data-testid="upcoming-events-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-3 h-3 rounded-full bg-[#22C55E]" />
            <h2 className="font-heading text-xl font-bold text-[#0A0A0A]">Upcoming Events</h2>
          </div>

          {upcoming.length === 0 ? (
            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-10 text-center" data-testid="no-upcoming-events">
              <Calendar className="w-8 h-8 text-[#CBD5E1] mx-auto mb-3" />
              <p className="text-sm font-medium text-[#94A3B8]">No upcoming events right now</p>
              <p className="text-xs text-[#CBD5E1] mt-1">Check back soon — we're always planning something meaningful.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {upcoming.map((event) => {
                const isPaid = event.ticket_type === 'paid' && event.ticket_price > 0;
                return (
                  <div key={event.id} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-testid={`upcoming-event-${event.id}`}>
                    <div className="md:flex">
                      <div className="md:w-64 h-56 md:h-auto flex-shrink-0">
                        {event.flyer_image ? (
                          <img src={event.flyer_image} alt={event.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-[#F8F9FA] border-r border-dashed border-[#E2E8F0] flex flex-col items-center justify-center p-6">
                            <ImageIcon className="w-6 h-6 text-[#CBD5E1] mb-2" />
                            <p className="text-xs text-[#94A3B8] text-center">Event flyer coming soon</p>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-5">
                        <div className="flex items-center gap-2 mb-2">
                          {event.is_active && <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>}
                          <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {isPaid ? `$${event.ticket_price}` : 'Free'}
                          </span>
                        </div>
                        <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-2">{event.title}</h3>
                        <div className="flex flex-wrap gap-3 text-xs text-[#4B5563] mb-3">
                          {event.date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[#BA1826]" /> {event.date}</span>}
                          {event.time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#BA1826]" /> {event.time}</span>}
                          {event.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#BA1826]" /> {event.location}</span>}
                        </div>
                        {event.description && <p className="text-sm text-[#4B5563] mb-3 line-clamp-3">{event.description}</p>}
                        <div className="flex items-center gap-3 mb-4">
                          {event.rsvp_count > 0 && (
                            <span className="text-xs text-[#4B5563] flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {event.rsvp_count} registered</span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Link to={`/events/${event.id}`} className="btn-primary text-xs px-4 py-2 inline-flex" data-testid={`event-link-${event.id}`}>
                            View Details <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                          {event.rsvp_enabled && (
                            <Link to={`/events/${event.id}`} className="btn-secondary text-xs px-4 py-2 inline-flex" data-testid={`rsvp-btn-${event.id}`}>
                              {isPaid ? 'Get Tickets' : 'RSVP Now'}
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Featured Event Photo Gallery */}
      <FeaturedGallery gallery={featuredGallery} photos={featuredPhotos} />

      {/* Past Events */}
      {completed.length > 0 && (
        <section className="py-10 bg-[#F8F9FA]" data-testid="past-events-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-3 h-3 rounded-full bg-[#94A3B8]" />
              <h2 className="font-heading text-xl font-bold text-[#0A0A0A]">Past Events</h2>
            </div>
            <div className="space-y-6">
              {completed.map((event) => (
                <div key={event.id} className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`past-event-${event.id}`}>
                  <div className="p-5 border-b border-[#E5E7EB]">
                    <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded mb-2 inline-block">Completed</span>
                    <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">{event.title}</h3>
                    <div className="flex flex-wrap gap-3 text-xs text-[#4B5563] mt-1">
                      {event.date && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {event.date}</span>}
                      {event.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {event.location}</span>}
                    </div>
                  </div>
                  {event.recap_text && (
                    <div className="px-5 pt-4">
                      <p className="text-sm text-[#4B5563] leading-relaxed">{event.recap_text}</p>
                    </div>
                  )}
                  {event.event_photos && event.event_photos.length > 0 && (
                    <div className="px-5 pb-5 pt-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {event.event_photos.map((photo, idx) => (
                          <button key={photo}
                            onClick={() => setLightbox({ open: true, photos: event.event_photos, idx, title: event.title })}
                            className="aspect-square rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
                            data-testid={`recap-photo-${event.id}-${idx}`}>
                            <img src={photo} alt={`${event.title} photo`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-10 bg-[#BA1826]" data-testid="events-cta">
        <div className="px-6 text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-3">Want to Support Our Next Event?</h2>
          <p className="text-white/80 max-w-md mx-auto mb-6 text-sm">
            Your donations and presence make every event possible. Together, we create real impact.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/donate" className="bg-white text-[#BA1826] px-6 py-2 font-semibold rounded hover:bg-gray-100 transition-all text-sm" data-testid="events-donate-btn">Donate Now</Link>
            <Link to="/involved" className="bg-transparent text-white border-2 border-white px-6 py-2 font-semibold rounded hover:bg-white/10 transition-all text-sm" data-testid="events-involved-btn">Get Involved</Link>
          </div>
        </div>
      </section>

      {/* Phase D: unified PhotoLightbox — prev/next through the event's
          recap photo story, object-contain, ESC + backdrop close,
          mobile-safe height clamp. */}
      <PhotoLightbox
        photos={lightbox.photos}
        open={lightbox.open}
        startIndex={lightbox.idx}
        title={lightbox.title}
        onClose={() => setLightbox((s) => ({ ...s, open: false }))}
      />
    </div>
  );
};

export default EventsPage;
